import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import ItemCard from "./ItemCard";
import { toast } from "sonner";
import { Copy, Pencil, X } from "lucide-react";

const TYPES = ["weapon", "armor", "consumable", "gear", "treasure"];
const RARITIES = ["common", "uncommon", "rare", "legendary"];
const EMPTY_EFFECT = { message: "", damage_dice: "", damage_type: "", apply_condition_slugs: [] };
const EMPTY_FORM = { name: "", type: "gear", rarity: "common", traits: "", description: "", bonus_text: "", damage_dice: "", ac_bonus: 0, bulk: "", cost: "", price_amount: 0, price_currency: "gp", range_ft: "" };

// Every item card the GM has made, plus the ability to Edit it in place, Clone→Edit a copy
// (so tweaks never touch the original card other tables/loot already reference), and Give it
// straight to a chest, an NPC's loot, a shopkeeper's stock, OR directly into a player's inventory.
export default function GmItemsTab({ game, scene, tokens, members }) {
  const [cards, setCards] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [onHitEffect, setOnHitEffect] = useState(EMPTY_EFFECT);
  const [conditions, setConditions] = useState([]);
  const [giveTargetId, setGiveTargetId] = useState(null);
  const [assignShopId, setAssignShopId] = useState(null);
  const [shopInfinite, setShopInfinite] = useState(true);
  const [shopQuantity, setShopQuantity] = useState(1);

  const loadCards = () => api.get(`/games/${game.id}/item-cards`).then((r) => setCards(r.data));
  useEffect(() => { loadCards(); api.get("/pf2e/conditions").then((r) => setConditions(r.data)).catch(() => setConditions([])); }, [game.id]);

  const lootTargets = (tokens || []).filter((t) => ["chest", "npc"].includes(t.token_type));
  const shopkeepers = (tokens || []).filter((t) => t.token_type === "npc" && t.is_shopkeeper);
  const playerMembers = (members || []).filter((m) => m.role !== "gm" && m.character_id);

  // Duplicate-named chests/NPCs (e.g. two "Treasure Chest" tokens) are otherwise indistinguishable
  // in these dropdowns — append a short id suffix only when a name actually collides.
  const disambiguate = (list, item) => {
    const dupes = list.filter((o) => o.name === item.name);
    return dupes.length > 1 ? `${item.name} #${item.id.slice(-4)}` : item.name;
  };

  const toggleEffectCondition = (slug) => {
    const has = onHitEffect.apply_condition_slugs.includes(slug);
    setOnHitEffect({ ...onHitEffect, apply_condition_slugs: has ? onHitEffect.apply_condition_slugs.filter((s) => s !== slug) : [...onHitEffect.apply_condition_slugs, slug] });
  };

  const buildPayload = () => ({
    ...form, traits: form.traits.split(",").map((t) => t.trim()).filter(Boolean), ac_bonus: parseInt(form.ac_bonus, 10) || 0, price_amount: parseInt(form.price_amount, 10) || 0,
    range_ft: form.type === "weapon" && form.range_ft ? parseFloat(form.range_ft) : null,
    on_hit_effect: form.type === "weapon" && (onHitEffect.message || onHitEffect.damage_dice || onHitEffect.apply_condition_slugs.length)
      ? { ...onHitEffect, message: onHitEffect.message.trim() || null, damage_dice: onHitEffect.damage_dice.trim() || null, damage_type: onHitEffect.damage_type.trim() || null }
      : null,
  });

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setOnHitEffect(EMPTY_EFFECT);
    setEditingId(null);
  };

  const submitCard = async () => {
    if (!form.name.trim()) return toast.error("Give the item a name");
    if (editingId) {
      await api.patch(`/games/${game.id}/item-cards/${editingId}`, buildPayload());
      toast.success("Item card updated");
    } else {
      await api.post(`/games/${game.id}/item-cards`, buildPayload());
      toast.success("Item card created");
    }
    resetForm();
    loadCards();
  };

  const startEdit = (card) => {
    setEditingId(card.id);
    setForm({
      name: card.name, type: card.type, rarity: card.rarity, traits: (card.traits || []).join(", "), description: card.description || "",
      bonus_text: card.bonus_text || "", damage_dice: card.damage_dice || "", ac_bonus: card.ac_bonus || 0, bulk: card.bulk || "", cost: card.cost || "",
      price_amount: card.price_amount || 0, price_currency: card.price_currency || "gp", range_ft: card.range_ft || "",
    });
    setOnHitEffect(card.on_hit_effect || EMPTY_EFFECT);
  };

  const cloneCard = async (card) => {
    const { id, game_id, created_at, ...rest } = card;
    const { data } = await api.post(`/games/${game.id}/item-cards`, { ...rest, name: `${card.name} (Copy)` });
    toast.success(`Cloned "${card.name}" — edit the copy freely, the original is untouched`);
    await loadCards();
    startEdit(data);
  };

  const deleteCard = async (id) => {
    await api.delete(`/games/${game.id}/item-cards/${id}`);
    if (editingId === id) resetForm();
    loadCards();
  };

  const giveItem = async (card) => {
    if (!giveTargetId) return toast.error("Pick a chest, NPC, or player first");
    if (giveTargetId.startsWith("member:")) {
      const member = playerMembers.find((m) => `member:${m.id}` === giveTargetId);
      if (!member) return;
      const { data: char } = await api.get(`/characters/${member.character_id}`);
      const inventory = [...(char.inventory || []), { ...card, equipped: false, is_card: true }];
      await api.patch(`/characters/${member.character_id}`, { inventory });
      toast.success(`Gave "${card.name}" directly to ${member.display_name}'s inventory`);
    } else {
      const target = lootTargets.find((t) => t.id === giveTargetId);
      if (!target) return;
      await api.patch(`/games/${game.id}/tokens/${giveTargetId}`, { loot: [...(target.loot || []), card] });
      toast.success(`Added "${card.name}" to ${target.name}'s loot`);
    }
  };

  const addToShop = async (card) => {
    if (!assignShopId) return toast.error("Pick a shopkeeper first");
    const shop = shopkeepers.find((t) => t.id === assignShopId);
    const stockItem = { ...card, infinite: shopInfinite, quantity: shopInfinite ? null : Math.max(1, shopQuantity) };
    await api.patch(`/games/${game.id}/tokens/${assignShopId}`, { shop_stock: [...(shop.shop_stock || []), stockItem] });
    toast.success(`Added "${card.name}" to ${shop.name}'s shop`);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <p className="text-[10px] uppercase text-[#f1e0c5]/40">{editingId ? "Edit Item Card" : "Create Item Card"}</p>
        <Input data-testid="item-card-name-input" placeholder="Item name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/30 border-white/10 h-8 text-xs" />
        <div className="flex gap-1.5">
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger data-testid="item-card-type-select" className="bg-black/30 border-white/10 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={form.rarity} onValueChange={(v) => setForm({ ...form, rarity: v })}>
            <SelectTrigger data-testid="item-card-rarity-select" className="bg-black/30 border-white/10 h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{RARITIES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <Input data-testid="item-card-traits-input" placeholder="Traits (comma separated)" value={form.traits} onChange={(e) => setForm({ ...form, traits: e.target.value })} className="bg-black/30 border-white/10 h-8 text-xs" />
        <Input data-testid="item-card-bonus-input" placeholder="Bonus text, e.g. +1 attack/damage" value={form.bonus_text} onChange={(e) => setForm({ ...form, bonus_text: e.target.value })} className="bg-black/30 border-white/10 h-8 text-xs" />
        <div className="flex gap-1.5">
          <Input data-testid="item-card-damage-input" placeholder="Damage dice (e.g. 1d8)" value={form.damage_dice} onChange={(e) => setForm({ ...form, damage_dice: e.target.value })} className="bg-black/30 border-white/10 h-8 text-xs" />
          <Input data-testid="item-card-ac-input" type="number" placeholder="AC bonus" value={form.ac_bonus} onChange={(e) => setForm({ ...form, ac_bonus: e.target.value })} className="bg-black/30 border-white/10 h-8 text-xs w-24" />
        </div>
        {form.type === "weapon" && (
          <div className="space-y-1.5 bg-black/20 border border-white/10 rounded-xl p-2.5">
            <Input data-testid="item-card-range-input" type="number" placeholder="Range in ft (blank = melee 5ft)" value={form.range_ft} onChange={(e) => setForm({ ...form, range_ft: e.target.value })} className="bg-black/30 border-white/10 h-8 text-xs" />
            <p className="text-[10px] uppercase text-[#D4AF37]/70">On-Hit Effect (GM-only — sticks to this item even when sold/traded)</p>
            <Textarea data-testid="item-card-effect-message-input" value={onHitEffect.message} onChange={(e) => setOnHitEffect({ ...onHitEffect, message: e.target.value })} placeholder="Text shown on a hit (optional)" className="bg-black/30 border-white/10 text-xs min-h-8" />
            <div className="flex gap-1.5">
              <Input data-testid="item-card-effect-damage-dice-input" value={onHitEffect.damage_dice} onChange={(e) => setOnHitEffect({ ...onHitEffect, damage_dice: e.target.value })} placeholder="Extra damage e.g. 1d4" className="bg-black/30 border-white/10 h-8 text-xs flex-1" />
              <Input data-testid="item-card-effect-damage-type-input" value={onHitEffect.damage_type} onChange={(e) => setOnHitEffect({ ...onHitEffect, damage_type: e.target.value })} placeholder="Type e.g. poison" className="bg-black/30 border-white/10 h-8 text-xs flex-1" />
            </div>
            <div className="flex flex-wrap gap-1">
              {conditions.map((c) => (
                <button
                  key={c.slug} type="button" data-testid={`item-card-effect-condition-${c.slug}`}
                  onClick={() => toggleEffectCondition(c.slug)}
                  className={`text-[9px] px-2 py-0.5 rounded-full border ${onHitEffect.apply_condition_slugs.includes(c.slug) ? "bg-[#D90429] border-[#D90429] text-white" : "border-white/10 text-[#f1e0c5]/50"}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="flex gap-1.5">
          <Input data-testid="item-card-bulk-input" placeholder="Bulk" value={form.bulk} onChange={(e) => setForm({ ...form, bulk: e.target.value })} className="bg-black/30 border-white/10 h-8 text-xs" />
          <Input data-testid="item-card-cost-input" placeholder="Cost (flavor text)" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="bg-black/30 border-white/10 h-8 text-xs" />
        </div>
        <div className="flex gap-1.5">
          <Input data-testid="item-card-price-input" type="number" placeholder="Shop price" value={form.price_amount} onChange={(e) => setForm({ ...form, price_amount: e.target.value })} className="bg-black/30 border-white/10 h-8 text-xs flex-1" />
          <Select value={form.price_currency} onValueChange={(v) => setForm({ ...form, price_currency: v })}>
            <SelectTrigger data-testid="item-card-price-currency-select" className="bg-black/30 border-white/10 h-8 text-xs w-20"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cp">cp</SelectItem>
              <SelectItem value="sp">sp</SelectItem>
              <SelectItem value="gp">gp</SelectItem>
              <SelectItem value="pp">pp</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Textarea data-testid="item-card-description-input" placeholder="Description / flavor text" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-black/30 border-white/10 text-xs min-h-16" />
        <div className="flex gap-1.5">
          <Button data-testid="item-card-create-button" onClick={submitCard} size="sm" variant="secondary" className="flex-1 rounded-full">{editingId ? "Save Changes" : "Create Card"}</Button>
          {editingId && <Button data-testid="item-card-cancel-edit-button" onClick={resetForm} size="sm" variant="ghost" className="rounded-full">Cancel</Button>}
        </div>
      </div>

      <div className="border-t border-white/10 pt-3 space-y-1.5">
        <p className="text-[10px] uppercase text-[#f1e0c5]/40">Give or Stock an Item</p>
        <Select value={giveTargetId || "none"} onValueChange={setGiveTargetId}>
          <SelectTrigger data-testid="item-give-target-select" className="bg-black/30 border-white/10 text-xs"><SelectValue placeholder="Choose a chest, NPC, or player" /></SelectTrigger>
          <SelectContent>
            {lootTargets.map((t) => <SelectItem key={t.id} value={t.id}>{t.token_type === "chest" ? "Chest" : "NPC"}: {disambiguate(lootTargets, t)}</SelectItem>)}
            {playerMembers.map((m) => <SelectItem key={m.id} value={`member:${m.id}`}>Player: {m.display_name}</SelectItem>)}
          </SelectContent>
        </Select>
        {shopkeepers.length > 0 && (
          <>
            <Select value={assignShopId || "none"} onValueChange={setAssignShopId}>
              <SelectTrigger data-testid="gm-shop-select" className="bg-black/30 border-white/10 text-xs"><SelectValue placeholder="Or stock a shopkeeper's shop" /></SelectTrigger>
              <SelectContent>{shopkeepers.map((c) => <SelectItem key={c.id} value={c.id}>{disambiguate(shopkeepers, c)} ({(c.shop_stock || []).length} items)</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-1.5 text-[10px] text-[#f1e0c5]/60 cursor-pointer shrink-0">
                <input data-testid="gm-shop-stock-infinite-checkbox" type="checkbox" checked={shopInfinite} onChange={(e) => setShopInfinite(e.target.checked)} className="accent-[#4d7c0f]" />
                Infinite stock
              </label>
              {!shopInfinite && (
                <Input data-testid="gm-shop-stock-quantity-input" type="number" min={1} value={shopQuantity} onChange={(e) => setShopQuantity(parseInt(e.target.value, 10) || 1)} className="bg-black/30 border-white/10 h-7 text-xs w-20" placeholder="Qty" />
              )}
            </div>
          </>
        )}
      </div>

      <div className="border-t border-white/10 pt-3 space-y-2">
        <p className="text-[10px] uppercase text-[#f1e0c5]/40">Card Library ({cards.length})</p>
        {cards.map((c) => (
          <ItemCard
            key={c.id}
            card={c}
            testId={`gm-item-card-${c.id}`}
            actions={
              <>
                <Button data-testid={`edit-item-card-${c.id}`} size="sm" variant="secondary" className="rounded-full text-xs h-7 gap-1" onClick={() => startEdit(c)}><Pencil className="w-3 h-3" /> Edit</Button>
                <Button data-testid={`clone-item-card-${c.id}`} size="sm" variant="secondary" className="rounded-full text-xs h-7 gap-1" onClick={() => cloneCard(c)}><Copy className="w-3 h-3" /> Clone</Button>
                <Button data-testid={`give-item-card-${c.id}`} size="sm" variant="secondary" className="rounded-full text-xs h-7" onClick={() => giveItem(c)}>Give</Button>
                {shopkeepers.length > 0 && <Button data-testid={`assign-to-shop-${c.id}`} size="sm" variant="secondary" className="rounded-full text-xs h-7" onClick={() => addToShop(c)}>To Shop</Button>}
                <button data-testid={`delete-item-card-${c.id}`} onClick={() => deleteCard(c.id)} className="text-[#f1e0c5]/30 hover:text-[#D90429]"><X className="w-3.5 h-3.5" /></button>
              </>
            }
          />
        ))}
        {cards.length === 0 && <p className="text-xs text-[#f1e0c5]/30 italic">No item cards yet — create one above.</p>}
      </div>
    </div>
  );
}
